// Tiny fetch helper that understands our `{ success, data, error }` envelope.
// Used by React Query hooks for /v1/* endpoints.

import { apiBaseUrl } from "./runtime-config";
import { authToken, setAuthToken } from "./auth-token";
import { currentScope } from "./scope";

const baseURL = apiBaseUrl();

export interface ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;
}

function toApiError(status: number, body: unknown): ApiError {
  const err = new Error("API error") as ApiError;
  err.status = status;
  if (
    body &&
    typeof body === "object" &&
    "error" in body &&
    body.error &&
    typeof body.error === "object"
  ) {
    const e = body.error as { code?: string; message?: string; details?: unknown };
    err.code = e.code ?? "unknown_error";
    err.message = e.message ?? "API error";
    err.details = e.details;
  } else {
    err.code = "network_error";
    err.message = `Request failed (${status})`;
  }
  return err;
}

export async function api<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, ...rest } = init ?? {};
  const headers = new Headers(rest.headers);
  if (json !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const token = authToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  // Attach the active workspace/project to every /v1/* call. Routes that
  // don't need them (e.g. /v1/me, /v1/workspaces) ignore the headers;
  // routes that do require them will 400 if absent.
  const scope = currentScope();
  if (scope.workspaceId && !headers.has("X-Workspace-Id")) {
    headers.set("X-Workspace-Id", scope.workspaceId);
  }
  if (scope.projectId && !headers.has("X-Project-Id")) {
    headers.set("X-Project-Id", scope.projectId);
  }
  const res = await fetch(`${baseURL}${path}`, {
    ...rest,
    // credentials:"include" lets the session cookie travel alongside
    // the bearer header. Bearer remains primary; the cookie is the
    // post-social-signin bridge — once the SPA has the bearer it
    // doesn't depend on the cookie for anything.
    credentials: rest.credentials ?? "include",
    headers,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  // Pick up a rotated session token if the server issued one.
  const refreshed = res.headers.get("set-auth-token");
  if (refreshed) setAuthToken(refreshed);

  // 204 → resolve undefined (callers assert on shape).
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? JSON.parse(text) : null;

  if (!res.ok) throw toApiError(res.status, body);

  if (body && typeof body === "object" && "success" in body) {
    if (body.success === true) return body.data as T;
    throw toApiError(res.status, body);
  }
  // Unenveloped JSON (shouldn't happen on /v1/* but allow for /health).
  return body as T;
}
